import { FC, ReactNode } from 'react';
import { Icon, IconName, Button, BackHeader, SkipToContentTarget } from 'src/components';
import { Color } from 'src/utils/theme';
import * as S from './styles';

export interface PreviewScreenProps {
  title: string;
  description: string;
  children: ReactNode;
  iconName?: IconName;
  iconColor?: Color;
  onClick?: () => void;
  buttonText?: string;
  /** Omits the play glyph on the primary CTA. */
  hideButtonIcon?: boolean;
  buddyIcon?: ReactNode;
  /** Sits alongside the header copy, for screens leading with a video or image rather than a buddy. */
  media?: ReactNode;
  backHref?: string;
  /** When true, omit standalone page chrome so the screen can sit inside DashboardFrame. */
  embedded?: boolean;
}

export const PreviewScreen: FC<PreviewScreenProps> = ({
  title,
  description,
  iconName,
  iconColor = 'neutral0',
  children,
  onClick,
  buttonText,
  hideButtonIcon = false,
  buddyIcon,
  media,
  backHref,
  embedded = false,
}) => {
  const headerCopy = (
    <>
      <S.TitleContainer>
        {iconName && (
          <S.IconContainer $color={iconColor}>
            <Icon name={iconName} />
          </S.IconContainer>
        )}
        <S.Title>{title}</S.Title>
      </S.TitleContainer>
      <S.Description>{description}</S.Description>
    </>
  );

  return (
    <>
      {backHref && !embedded && (
        <S.StickyHeader>
          <BackHeader href={backHref} />
        </S.StickyHeader>
      )}
      <SkipToContentTarget />
      <S.Container $embedded={embedded}>
        <S.ContentWrapper>
          <S.Header>
            {media ? (
              <S.HeaderRow>
                <S.HeaderCopy>{headerCopy}</S.HeaderCopy>
                <S.HeaderMedia>{media}</S.HeaderMedia>
              </S.HeaderRow>
            ) : (
              <>
                {headerCopy}
                {buddyIcon}
              </>
            )}
          </S.Header>
          <S.Content>{children}</S.Content>
          {buttonText && (
            <S.StickyWrapper>
              <S.ButtonsContainer>
                <Button
                  data-testid="start_of_session_primary_cta"
                  buttonType="primary"
                  buttonSize="large"
                  onClick={onClick}
                  wide
                >
                  {!hideButtonIcon && <S.StartIcon name="playFilled" />}
                  {buttonText}
                </Button>
              </S.ButtonsContainer>
            </S.StickyWrapper>
          )}
        </S.ContentWrapper>
      </S.Container>
    </>
  );
};
